import { useEffect, useState } from "react";
import { CheckCircle2, Facebook, Instagram, Loader2, Plug, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const fbSteps = [
  "Go to developers.facebook.com and sign in with the account that manages your page.",
  "Click My Apps, then Create App - choose \"Business\" and name it (e.g. Quest K9 Website).",
  "In your new app, open Tools, then Graph API Explorer.",
  "Press \"Generate Access Token\", tick pages_show_list, pages_manage_posts and pages_read_engagement, then continue and allow.",
  "Copy the long token at the top and paste it below.",
];

const xSteps = [
  "Go to developer.x.com and sign in with your X account, then create a Project and App.",
  "In the app's \"User authentication settings\", enable OAuth 2.0 with Read and write.",
  "Under \"Keys and tokens\", generate an Access Token and Secret with tweet.read, tweet.write, users.read, media.write, offline.access.",
  "Paste the access token below.",
];

export default function SocialTab() {
  const [status, setStatus] = useState(null);
  const [fbToken, setFbToken] = useState("");
  const [fbAppId, setFbAppId] = useState("");
  const [fbAppSecret, setFbAppSecret] = useState("");
  const [pages, setPages] = useState(null);
  const [xToken, setXToken] = useState("");
  const [links, setLinks] = useState({ facebook_url: "", x_url: "", linkedin_url: "", instagram_url: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .get("/social/status")
      .then((r) => {
        setStatus(r.data);
        setLinks(r.data?.links || { facebook_url: "", x_url: "", linkedin_url: "", instagram_url: "" });
      })
      .catch(() => setStatus(null));
  };
  useEffect(load, []);

  const saveLinks = async () => {
    setBusy(true);
    try {
      await api.put("/social/links", {
        facebook_url: links.facebook_url.trim(),
        x_url: links.x_url.trim(),
        linkedin_url: links.linkedin_url.trim(),
        instagram_url: links.instagram_url.trim(),
      });
      toast.success("Footer links saved - they now show on the website footer");
      load();
    } catch (e) {
      toast.error("Could not save links", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const findPages = async () => {
    if (!fbToken.trim()) {
      toast.error("Paste your Facebook access token first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/social/facebook/pages", { access_token: fbToken.trim(), app_id: fbAppId.trim(), app_secret: fbAppSecret.trim() });
      setPages(data.pages || []);
      if (!data.pages?.length) toast.error("No pages found for that token - make sure you manage a page and ticked the permissions");
    } catch (e) {
      toast.error("Could not reach Facebook", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const connectPage = async (page) => {
    setBusy(true);
    try {
      const { data } = await api.post("/social/facebook/connect", { page_id: page.id, page_name: page.name, access_token: page.access_token });
      toast.success(`Connected to ${data.page_name}`);
      setPages(null);
      setFbToken("");
      setFbAppId("");
      setFbAppSecret("");
      load();
    } catch (e) {
      toast.error("Could not connect the page", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const connectX = async () => {
    if (!xToken.trim()) {
      toast.error("Paste your X access token first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/social/x/connect", { access_token: xToken.trim() });
      toast.success(`Connected to @${data.username}`);
      setXToken("");
      load();
    } catch (e) {
      toast.error("Could not connect X", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (channel) => {
    try {
      await api.delete(`/social/${channel}`);
      toast.success(channel === "facebook" ? "Facebook disconnected" : "X disconnected");
      load();
    } catch (e) {
      toast.error("Could not disconnect", { description: formatApiError(e) });
    }
  };

  if (status === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="social-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="social-tab">
      <p className="text-sm text-white/50 mb-6">
        Connect your accounts here. When you write a blog post, you can tick which channels to post to at the same time - with the title, the image and a link back to your website.
      </p>

      <div className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="social-facebook-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#1877F2]/15 border border-[#1877F2]/40">
              <Facebook className="h-5 w-5 text-[#1877F2]" />
            </div>
            <div>
              <p className="font-display font-bold text-white">Facebook Page</p>
              {status.facebook?.connected ? (
                <p className="text-xs text-emerald-400 flex items-center gap-1.5 mt-0.5" data-testid="facebook-connected-as">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Connected to {status.facebook.page_name}
                </p>
              ) : (
                <p className="text-xs text-white/50 mt-0.5">Not connected</p>
              )}
            </div>
          </div>
          {status.facebook?.connected && (
            <Button variant="outline" onClick={() => disconnect("facebook")} data-testid="facebook-disconnect" className="border-brand-line text-white/60 hover:text-white hover:bg-white/5 text-xs">
              Disconnect
            </Button>
          )}
        </div>

        {!status.facebook?.connected && (
          <div className="mt-5 space-y-4">
            <ol className="space-y-1.5 text-sm text-white/60 list-decimal list-inside">
              {fbSteps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Facebook access token</Label>
              <Input data-testid="facebook-token-input" value={fbToken} onChange={(e) => setFbToken(e.target.value)} placeholder="Paste the token from Graph API Explorer" className="bg-brand-obsidian border-brand-line" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">App ID (optional - recommended)</Label>
                <Input data-testid="facebook-app-id" value={fbAppId} onChange={(e) => setFbAppId(e.target.value)} placeholder="From Settings > Basic" className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">App Secret (optional - recommended)</Label>
                <Input data-testid="facebook-app-secret" type="password" value={fbAppSecret} onChange={(e) => setFbAppSecret(e.target.value)} placeholder="From Settings > Basic" className="bg-brand-obsidian border-brand-line" />
              </div>
            </div>
            <p className="text-[11px] text-white/40">Adding the App ID and Secret keeps the connection alive for months instead of hours. They are stored securely in your own database.</p>
            <Button onClick={findPages} disabled={busy} data-testid="facebook-find-pages" className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold">
              <Plug className="h-4 w-4" /> Find My Pages
            </Button>
            {pages?.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 rounded-md border border-brand-line bg-brand-obsidian p-3" data-testid={`facebook-page-${p.id}`}>
                <span className="text-sm text-white">{p.name}</span>
                <Button onClick={() => connectPage(p)} disabled={busy} data-testid={`facebook-connect-${p.id}`} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold">
                  Connect
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="social-x-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10 border border-white/20">
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-white" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </div>
            <div>
              <p className="font-display font-bold text-white">X (Twitter)</p>
              {status.x?.connected ? (
                <p className="text-xs text-emerald-400 flex items-center gap-1.5 mt-0.5" data-testid="x-connected-as">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Connected to @{status.x.username}
                </p>
              ) : (
                <p className="text-xs text-white/50 mt-0.5">Not connected</p>
              )}
            </div>
          </div>
          {status.x?.connected && (
            <Button variant="outline" onClick={() => disconnect("x")} data-testid="x-disconnect" className="border-brand-line text-white/60 hover:text-white hover:bg-white/5 text-xs">
              Disconnect
            </Button>
          )}
        </div>

        {!status.x?.connected && (
          <div className="mt-5 space-y-4">
            <ol className="space-y-1.5 text-sm text-white/60 list-decimal list-inside">
              {xSteps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
            <p className="text-[11px] text-amber-400/80">Heads up: X charges for API posting on most plans. The Facebook connection is free.</p>
            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">X access token</Label>
              <Input data-testid="x-token-input" type="password" value={xToken} onChange={(e) => setXToken(e.target.value)} placeholder="Paste your X user access token" className="bg-brand-obsidian border-brand-line" />
            </div>
            <Button onClick={connectX} disabled={busy} data-testid="x-connect" className="bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold">
              <Plug className="h-4 w-4" /> Connect X
            </Button>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="social-instagram-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#E1306C]/15 border border-[#E1306C]/40">
              <Instagram className="h-5 w-5 text-[#E1306C]" />
            </div>
            <div>
              <p className="font-display font-bold text-white">Instagram</p>
              {status.instagram?.connected ? (
                <p className="text-xs text-emerald-400 flex items-center gap-1.5 mt-0.5" data-testid="instagram-connected-as">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Connected via your Facebook page ({status.instagram.page_name})
                </p>
              ) : (
                <p className="text-xs text-white/50 mt-0.5">Not connected</p>
              )}
            </div>
          </div>
          {status.instagram?.connected && (
            <Button variant="outline" onClick={() => disconnect("instagram")} data-testid="instagram-disconnect" className="border-brand-line text-white/60 hover:text-white hover:bg-white/5 text-xs">
              Disconnect
            </Button>
          )}
        </div>

        {!status.instagram?.connected && (
          <div className="mt-5 space-y-4">
            <ol className="space-y-1.5 text-sm text-white/60 list-decimal list-inside">
              <li>In the Instagram app, switch your account to Professional (free) - Settings, then Account type and tools.</li>
              <li>Link it to your Facebook page - Settings, then Business tools and resources, then Connect a Facebook page.</li>
              <li>Connect Facebook above - Instagram connects automatically when the page is linked.</li>
            </ol>
            <p className="text-[11px] text-white/40">Instagram posts need a cover image on the blog post, and links in Instagram captions are shown as text rather than tappable links.</p>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="social-links-card">
        <p className="font-display font-bold text-white">Your page links (shown as follow buttons in the website footer)</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Facebook page link</Label>
            <Input data-testid="facebook-url-input" value={links.facebook_url || ""} onChange={(e) => setLinks({ ...links, facebook_url: e.target.value })} placeholder="https://facebook.com/yourpage" className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">X profile link</Label>
            <Input data-testid="x-url-input" value={links.x_url || ""} onChange={(e) => setLinks({ ...links, x_url: e.target.value })} placeholder="https://x.com/yourhandle" className="bg-brand-obsidian border-brand-line" />
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">LinkedIn profile / company link</Label>
            <Input data-testid="linkedin-url-input" value={links.linkedin_url || ""} onChange={(e) => setLinks({ ...links, linkedin_url: e.target.value })} placeholder="https://www.linkedin.com/company/yourname" className="bg-brand-obsidian border-brand-line" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Instagram profile link</Label>
            <Input data-testid="instagram-url-input" value={links.instagram_url || ""} onChange={(e) => setLinks({ ...links, instagram_url: e.target.value })} placeholder="https://instagram.com/yourhandle" className="bg-brand-obsidian border-brand-line" />
          </div>
        </div>
        <Button onClick={saveLinks} disabled={busy} data-testid="social-links-save" className="mt-4 bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold">
          Save Links
        </Button>
      </div>

      <p className="mt-6 text-[11px] text-white/40 flex items-center gap-2">
        <XCircle className="h-3.5 w-3.5 shrink-0" /> Tokens are stored securely in your own database and are never shown on screen again.
      </p>
    </div>
  );
}
